'use client';

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import { Box, CircularProgress } from '@mui/material';
import { useMemo, useState } from 'react';
import { Typography } from '@/primitives';
import { alpha } from '@mui/material/styles';
import { AnimalMediaItem } from '@/hooks/publicSearch/animal/useAnimalMedia';
import { MEDIA_KIND } from '@/publicSearch/utils/media';
import ImageLightbox from '@/components/publicSearch/ImageLightbox';

type AnimalMediaTabProps = {
  items: AnimalMediaItem[];
  animalName?: string | null;
  loading?: boolean;
};

function getSubtitle(item: AnimalMediaItem) {
  if (item.source === 'animal') {
    return 'Animal media';
  }

  const segments = [];
  if (item.saleName) segments.push(item.saleName);
  if (item.lotNo) segments.push(`Lot ${item.lotNo}`);
  return segments.join(' · ') || 'Lot media';
}

export default function AnimalMediaTab({
  items,
  animalName,
  loading = false,
}: AnimalMediaTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedIndex = useMemo(
    () => items.findIndex((item) => item.id === selectedId),
    [items, selectedId],
  );
  const selectedItem = selectedIndex >= 0 ? items[selectedIndex] : null;

  const prevItem = selectedIndex > 0 ? items[selectedIndex - 1] : undefined;
  const nextItem = selectedIndex >= 0 ? items[selectedIndex + 1] : undefined;
  const goToPrev = prevItem ? () => setSelectedId(prevItem.id) : undefined;
  const goToNext = nextItem ? () => setSelectedId(nextItem.id) : undefined;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography
          variant="bodysmall"
          sx={{
            color: 'text.secondary',
          }}
        >
          Loading media…
        </Typography>
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Typography
        variant="bodysmall"
        sx={{
          color: 'text.secondary',
        }}
      >
        No media available.
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          alignItems: 'start',
          gap: 2,
        }}
      >
        {items.map((item) => {
          const title = animalName || item.title;
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={() => setSelectedId(item.id)}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                overflow: 'hidden',
                padding: 0,
                textAlign: 'left',
                cursor: 'pointer',
                width: '100%',
                transition: (theme) =>
                  theme.transitions.create(
                    ['border-color', 'box-shadow', 'transform'],
                    { duration: theme.transitions.duration.shorter },
                  ),
                '&:hover': {
                  boxShadow: 1,
                  transform: 'translateY(-2px)',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              {item.kind === MEDIA_KIND.IMAGE ? (
                <Box
                  component="img"
                  src={item.proxyUrl}
                  alt={title}
                  loading="lazy"
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    backgroundColor: 'background.default',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    aspectRatio: '1 / 1',
                    backgroundColor: 'background.default',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayCircleOutlineIcon
                    sx={{ color: 'text.secondary', fontSize: 48 }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      px: 1,
                      py: 0.25,
                      borderRadius: 10,
                      backgroundColor: (theme) =>
                        alpha(theme.palette.common.black, 0.7),
                      backdropFilter: 'blur(4px)',
                      color: 'common.white',
                    }}
                  >
                    <Typography variant="bodysmall">Video</Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ p: 1.5 }}>
                {title && (
                  <Typography
                    variant="bodysmall"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={title}
                  >
                    {title}
                  </Typography>
                )}
                <Typography
                  variant="bodysmall"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {getSubtitle(item)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      {/* Images and videos both open in the shared lightbox. */}
      <ImageLightbox
        open={Boolean(selectedItem)}
        src={selectedItem?.proxyUrl}
        alt={animalName || selectedItem?.title}
        title={animalName || selectedItem?.title}
        subtitle={selectedItem ? getSubtitle(selectedItem) : undefined}
        mediaType={selectedItem?.kind === MEDIA_KIND.IMAGE ? 'image' : 'video'}
        onPrev={goToPrev}
        onNext={goToNext}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
